//---------------------------------------------------------
// Soft visual system — shared building blocks for the Soft pages.
// Every Soft page renders inside <SoftPage>, which applies the Soft theme
// (softTheme.ts). Fields, selects and autocompletes share one shell
// (<SoftField>): label above, control, one message line below.
//---------------------------------------------------------
import {
	Alert,
	AlertProps,
	Autocomplete,
	AutocompleteProps,
	Box,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	TextFieldProps,
	Tooltip,
} from '@mui/material';
import {ThemeProvider, useTheme} from '@mui/material/styles';
import ScrollableBox from 'components/layout/ScrollableBox';
import {ReactNode, useMemo} from 'react';
import {isEmptySelectValue, softFieldMessage, splitMonoRuns} from './softField';
import {SOFT, buildSoftTheme} from './softTheme';

const C = SOFT.color;
const S = SOFT.size;
const STACK_MEDIA = `@media (max-width: ${S.stackBelow - 0.05}px)`;

//---------------------------------------------------------
// Scope + page layout
//---------------------------------------------------------
export function SoftScope(props: {children?: ReactNode}) {
	const outer = useTheme();
	const theme = useMemo(() => buildSoftTheme(outer), [outer]);
	return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
}

export function SoftPage(props: {title?: ReactNode; actions?: ReactNode; children?: ReactNode}) {
	return (
		<ScrollableBox bgcolor={C.page}>
			<SoftScope>
				<Box
					sx={{
						padding: `${S.pagePad}px`,
						backgroundColor: C.page,
						color: C.text,
						fontFamily: SOFT.font.sans,
						minHeight: '100%',
						boxSizing: 'border-box',
					}}
				>
					{props.title !== undefined ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 2,
								marginBottom: `${S.titleGap}px`,
							}}
						>
							<SoftPageTitle>{props.title}</SoftPageTitle>
							{props.actions}
						</Box>
					) : null}
					{props.children}
				</Box>
			</SoftScope>
		</ScrollableBox>
	);
}

export function SoftPageTitle(props: {children?: ReactNode}) {
	return (
		<Box component="h1" sx={{...SOFT.type.pageTitle, color: C.text, margin: 0}}>
			{props.children}
		</Box>
	);
}

export function SoftSectionTitle(props: {children?: ReactNode}) {
	return (
		<Box component="h2" sx={{...SOFT.type.sectionTitle, color: C.text, margin: 0}}>
			{props.children}
		</Box>
	);
}

/** Small heading inside a section (e.g. "Add account"). Same type as a field label. */
export function SoftSubheading(props: {children?: ReactNode}) {
	return (
		<Box component="h3" sx={{...SOFT.type.value, fontWeight: 600, color: C.text, margin: 0}}>
			{props.children}
		</Box>
	);
}

/** A page section. Consecutive sections sit 32px apart; children 20px apart. */
export function SoftSection(props: {title?: ReactNode; actions?: ReactNode; children?: ReactNode}) {
	return (
		<Box
			component="section"
			className="soft-section"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: `${S.fieldGap}px`,
				'.soft-section + &': {marginTop: `${S.sectionGap}px`},
			}}
		>
			{props.title !== undefined || props.actions ? (
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
					{props.title !== undefined ? <SoftSectionTitle>{props.title}</SoftSectionTitle> : <span />}
					{props.actions}
				</Box>
			) : null}
			{props.children}
		</Box>
	);
}

/** Vertical rhythm for a form column: 20px from one field's helper to the next label. */
export function SoftStack(props: {children?: ReactNode; maxWidth?: number | string}) {
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: `${S.fieldGap}px`,
				width: '100%',
				maxWidth: props.maxWidth,
				minWidth: 0,
			}}
		>
			{props.children}
		</Box>
	);
}

export function SoftCard(props: {children?: ReactNode; padded?: boolean}) {
	return (
		<Paper
			variant="outlined"
			sx={{
				padding: props.padded === false ? 0 : `${S.cardPad}px`,
				borderRadius: `${S.cardRadius}px`,
				border: `1px solid ${C.divider}`,
				boxShadow: 'none',
				backgroundColor: C.surface,
				overflow: 'hidden',
			}}
		>
			{props.children}
		</Paper>
	);
}

/** Supporting copy: 13px/20px muted. */
export function SoftText(props: {children?: ReactNode}) {
	return <Box sx={{...SOFT.type.value, color: C.textMuted, margin: 0}}>{props.children}</Box>;
}

export function SoftMono(props: {children?: ReactNode}) {
	return (
		<Box component="span" sx={{fontFamily: SOFT.font.mono, fontSize: 'inherit', lineHeight: 'inherit'}}>
			{props.children}
		</Box>
	);
}

/** Copy with any IPv4/CIDR runs set in mono at the same size. */
export function SoftRunText(props: {text: string}) {
	return (
		<>
			{splitMonoRuns(props.text).map((run, i) => (run.mono ? <SoftMono key={i}>{run.text}</SoftMono> : <span key={i}>{run.text}</span>))}
		</>
	);
}

/** "Label (id)" with the id in mono. */
export function SoftLabelWithId(props: {label?: string; id: string}) {
	if (!props.label || props.label === props.id) return <SoftMono>{props.id}</SoftMono>;
	return (
		<span>
			{props.label} (<SoftMono>{props.id}</SoftMono>)
		</span>
	);
}

/** Page alert: full column width, 13px/20px, 18px icon, outline at 30%. */
export function SoftAlert(props: AlertProps) {
	return <Alert {...props} sx={{width: '100%', boxSizing: 'border-box', ...(props.sx as object)}} />;
}

//---------------------------------------------------------
// Fields
//---------------------------------------------------------
type MessageProps = {helper?: string | null; warning?: string | null; error?: string | null};

const MESSAGE_COLOR = {helper: C.textMuted, warning: C.warning, error: C.error} as const;

export function SoftField(props: MessageProps & {id: string; label: ReactNode; children: ReactNode}) {
	const msg = softFieldMessage(props);
	return (
		<Box className="soft-field" sx={{display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%'}}>
			<Box
				component="label"
				htmlFor={props.id}
				sx={{...SOFT.type.label, color: C.text, marginBottom: `${S.labelGap}px`, display: 'block'}}
			>
				{props.label}
			</Box>
			{props.children}
			{msg ? (
				<Box
					id={`${props.id}-msg`}
					role={msg.tone === 'helper' ? undefined : 'status'}
					sx={{...SOFT.type.helper, color: MESSAGE_COLOR[msg.tone], marginTop: `${S.helperGap}px`}}
				>
					{msg.text}
				</Box>
			) : null}
		</Box>
	);
}

function describedBy(id: string, props: MessageProps): string | undefined {
	return softFieldMessage(props) ? `${id}-msg` : undefined;
}

export type SoftTextFieldProps = MessageProps &
	Omit<TextFieldProps, 'label' | 'helperText' | 'error' | 'select' | 'variant'> & {
		id: string;
		label: ReactNode;
		mono?: boolean;
	};

export function SoftTextField(props: SoftTextFieldProps) {
	const {id, label, helper, warning, error, mono, slotProps, sx, ...rest} = props;
	const msgProps = {helper, warning, error};
	return (
		<SoftField id={id} label={label} {...msgProps}>
			<TextField
				{...rest}
				id={id}
				fullWidth
				error={Boolean(error)}
				slotProps={{
					...slotProps,
					htmlInput: {
						'aria-describedby': describedBy(id, msgProps),
						...((slotProps as any)?.htmlInput || {}),
					},
				}}
				sx={{
					...(mono ? {'& .MuiInputBase-input': {fontFamily: SOFT.font.mono, fontSize: 13, lineHeight: '20px'}} : {}),
					...(sx as object),
				}}
			/>
		</SoftField>
	);
}

export interface SoftOption {
	value: string;
	label: ReactNode;
	disabled?: boolean;
}

export function SoftSelect(
	props: MessageProps & {
		id: string;
		label: ReactNode;
		value: string;
		onChange: (value: string) => void;
		options: SoftOption[];
		placeholder?: string;
		disabled?: boolean;
	},
) {
	const {id, label, helper, warning, error, value, onChange, options, placeholder, disabled} = props;
	const msgProps = {helper, warning, error};
	return (
		<SoftField id={id} label={label} {...msgProps}>
			<TextField
				select
				id={id}
				value={value}
				onChange={e => onChange(e.target.value)}
				fullWidth
				disabled={disabled}
				error={Boolean(error)}
				slotProps={{
					select: {
						displayEmpty: true,
						renderValue: (v: unknown) =>
							isEmptySelectValue(v) ? (
								<Box component="span" sx={{color: C.placeholder}}>
									{placeholder || ''}
								</Box>
							) : (
								options.find(o => o.value === v)?.label ?? String(v)
							),
						inputProps: {'aria-describedby': describedBy(id, msgProps)},
					},
				}}
			>
				{options.map(o => (
					<MenuItem key={o.value} value={o.value} disabled={o.disabled}>
						{o.label}
					</MenuItem>
				))}
			</TextField>
		</SoftField>
	);
}

export type SoftAutocompleteProps<T, F extends boolean | undefined> = MessageProps &
	Omit<AutocompleteProps<T, false, false, F>, 'renderInput' | 'id'> & {
		id: string;
		label: ReactNode;
		placeholder?: string;
	};

export function SoftAutocomplete<T, F extends boolean | undefined = false>(props: SoftAutocompleteProps<T, F>) {
	const {id, label, helper, warning, error, placeholder, ...rest} = props;
	const msgProps = {helper, warning, error};
	return (
		<SoftField id={id} label={label} {...msgProps}>
			<Autocomplete<T, false, false, F>
				{...rest}
				id={id}
				fullWidth
				renderInput={params => (
					<TextField
						{...params}
						placeholder={placeholder}
						error={Boolean(error)}
						inputProps={{...params.inputProps, 'aria-describedby': describedBy(id, msgProps)}}
					/>
				)}
			/>
		</SoftField>
	);
}

/** Inline add row (Linode): controls side by side, 36px button aligned with them; stacks under 900px. */
export function SoftInlineRow(props: {children?: ReactNode; action: ReactNode}) {
	const labelOffset = SOFT.type.label.fontSize + 4 + S.labelGap; // 16px label line + 6px gap
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: '12px',
				'& > .soft-field': {flex: '1 1 0'},
				[STACK_MEDIA]: {flexDirection: 'column', alignItems: 'stretch', gap: `${S.fieldGap}px`},
			}}
		>
			{props.children}
			<Box sx={{flex: '0 0 auto', paddingTop: `${labelOffset}px`, [STACK_MEDIA]: {paddingTop: 0}}}>{props.action}</Box>
		</Box>
	);
}

//---------------------------------------------------------
// Preview rows
//---------------------------------------------------------
export function SoftPreviewRow(props: {label: ReactNode; value: ReactNode; mono?: boolean}) {
	return (
		<Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
			<Box sx={{...SOFT.type.helper, color: C.textMuted, flex: '0 0 auto'}}>{props.label}</Box>
			<Box
				sx={{
					...SOFT.type.value,
					color: C.text,
					textAlign: 'right',
					fontFamily: props.mono ? SOFT.font.mono : undefined,
					whiteSpace: props.mono ? 'nowrap' : 'normal',
					overflowWrap: 'normal',
					minWidth: 0,
				}}
			>
				{props.value}
			</Box>
		</Box>
	);
}

//---------------------------------------------------------
// Tables
//---------------------------------------------------------
export interface SoftColumn<R> {
	key: string;
	header: ReactNode;
	render: (row: R) => ReactNode;
	/** ids, last four, OS ids */
	mono?: boolean;
	/** secondary copy such as who saved the row */
	muted?: boolean;
	align?: 'left' | 'right';
	width?: number | string;
}

export function SoftTable<R>(props: {columns: SoftColumn<R>[]; rows: R[]; rowKey: (row: R) => string; empty: ReactNode; label?: string}) {
	const {columns, rows, rowKey, empty, label} = props;
	return (
		<SoftCard padded={false}>
			<Table aria-label={label}>
				<TableHead>
					<TableRow>
						{columns.map(col => (
							<TableCell key={col.key} align={col.align} sx={{width: col.width}}>
								{col.header}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={columns.length} sx={{color: C.textMuted}}>
								{empty}
							</TableCell>
						</TableRow>
					) : (
						rows.map(row => (
							<TableRow key={rowKey(row)} hover>
								{columns.map(col => (
									<TableCell
										key={col.key}
										align={col.align}
										sx={{
											fontFamily: col.mono ? SOFT.font.mono : undefined,
											color: col.muted ? C.textMuted : undefined,
											whiteSpace: col.align === 'right' ? 'nowrap' : undefined,
										}}
									>
										{col.render(row)}
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</SoftCard>
	);
}

/** 32px icon button with an 18px icon; destructive actions use the error color. */
export function SoftIconAction(props: {label: string; icon: ReactNode; onClick: () => void; disabled?: boolean; destructive?: boolean; tooltip?: string}) {
	return (
		<Tooltip title={props.tooltip || props.label}>
			<span>
				<IconButton
					aria-label={props.label}
					onClick={props.onClick}
					disabled={props.disabled}
					sx={{color: props.destructive ? C.error : C.textMuted}}
				>
					{props.icon}
				</IconButton>
			</span>
		</Tooltip>
	);
}
